import { FIVE_SECONDS_IN_MS } from '../../../constants/common';
import type {
  ChannelStateShort,
} from '../../../proto/generated/channel/channel_state_short';
import type {
  LocalChannelState,
  LocalChannelStatePeer,
  LocalChannelStatePeerConsumer,
  LocalChannelStatePeerProducer,
} from '../../../types/channelStateSyncronizer';
import ChannelStateConsistencyCheckResult from './ChannelStateConsistencyCheckResult';

interface ChannelStateConsistencyCheckerParams {
  localState: LocalChannelState;
  remoteState: ChannelStateShort;
}

class ChannelStateConsistencyChecker {
  private readonly localState: LocalChannelState;

  private readonly remoteState: ChannelStateShort;

  private missingPeers: string[] = [];

  private missingProducers: string[] = [];

  private consumersWithInconsistentState: string[] = [];

  private producersWithInconsistentState: string[] = [];

  private peersWithInconsistentAppData: string[] = [];

  constructor(params: ChannelStateConsistencyCheckerParams) {
    this.localState = params.localState;
    this.remoteState = params.remoteState;
  }

  check(): ChannelStateConsistencyCheckResult {
    this.resetPreviousCheckResults();
    this.remoteState.peers.forEach((remotePeer) => {
      const localPeer = this.findLocalPeerById(remotePeer.id);

      if (!localPeer) {
        this.missingPeers.push(remotePeer.id);

        return;
      }

      const isDifferentAppData = localPeer.appDataHash !== remotePeer.appDataHash;

      if (isDifferentAppData) {
        this.peersWithInconsistentAppData.push(remotePeer.id);
      }

      remotePeer.producers.forEach((remoteProducer) => {
        const localProducer = this.findLocalProducerById(localPeer, remoteProducer.id);

        if (!localProducer) {
          this.missingProducers.push(remoteProducer.id);

          return;
        }

        if (this.isLocalProducerStateChangedRecently(localProducer)) {
          return;
        }

        const isInconsistentState = localProducer.paused !== remoteProducer.paused;

        if (isInconsistentState) {
          this.producersWithInconsistentState.push(remoteProducer.id);

          // don't check consumer state while producer state is inconsistent
          return;
        }

        const localConsumer = this.findLocalConsumerByProducerId(localPeer, remoteProducer.id);

        if (localConsumer && localConsumer.paused !== remoteProducer.paused) {
          this.consumersWithInconsistentState.push(localConsumer.id);
        }

        if (!localProducer) {
          this.missingProducers.push(remoteProducer.id);
        }
      });
    });

    return new ChannelStateConsistencyCheckResult({
      missingPeers: this.missingPeers,
      missingProducers: this.missingProducers,
      consumersWithInconsistentState: this.consumersWithInconsistentState,
      producersWithInconsistentState: this.producersWithInconsistentState,
      peersWithInconsistentAppData: this.peersWithInconsistentAppData,
    });
  }

  getMissingPeers(): string[] {
    return this.missingPeers;
  }

  getMissingProducers(): string[] {
    return this.missingProducers;
  }

  getConsumersWithInconsistentState(): string[] {
    return this.consumersWithInconsistentState;
  }

  getProducersWithInconsistentState(): string[] {
    return this.producersWithInconsistentState;
  }

  getPeersWithInconsistentAppData(): string[] {
    return this.peersWithInconsistentAppData;
  }

  private findLocalPeerById(id: string): LocalChannelStatePeer | undefined {
    return this.localState.peers.find((item) => item.id === id);
  }

  private findLocalConsumerByProducerId(
    peer: LocalChannelStatePeer,
    producerId: string,
  ): LocalChannelStatePeerConsumer | undefined {
    return peer.consumers?.find((item) => item.producerId === producerId);
  }

  private findLocalProducerById(peer: LocalChannelStatePeer, id: string): LocalChannelStatePeerProducer | undefined {
    return peer.producers.find((item) => item.id === id);
  }

  private resetPreviousCheckResults(): void {
    this.missingPeers = [];
    this.missingProducers = [];
    this.consumersWithInconsistentState = [];
    this.producersWithInconsistentState = [];
    this.peersWithInconsistentAppData = [];
  }

  private isLocalProducerStateChangedRecently(producer: LocalChannelStatePeerProducer): boolean {
    return Date.now() - producer.stateChangedAt < FIVE_SECONDS_IN_MS;
  }
}

export default ChannelStateConsistencyChecker;
